import React, { useState, useEffect, useCallback } from 'react';
import { useBranch } from './../../layouts/BranchContext'; 
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Button,
  styled,
  Modal,
  IconButton,
  useTheme,
  Snackbar,
  CircularProgress,
  Chip,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  Tooltip,
  Popover,
} from '@mui/material';

import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Close as CloseIcon } from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import BarChartIcon from '@mui/icons-material/BarChart';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MuiAlert from '@mui/material/Alert';

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  width: '100%',
  '&::-webkit-scrollbar': {
    width: '6px',
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.2)'
        : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.3)'
        : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${
    theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.2)'
      : 'rgba(0,0,0,0.2)'
  } transparent`,
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return (
    <MuiAlert
      elevation={6}
      ref={ref}
      variant="filled"
      {...props}
    />
  );
});

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  'https://wims.revisetoexcel.com';

const STATUS_OPTIONS = [
  {
    value: 'present',
    label: 'Present',
    color: 'success',
  },
  {
    value: 'absent',
    label: 'Absent',
    color: 'error',
  },
  {
    value: 'half_day',
    label: 'Half Day',
    color: 'warning',
  },
  {
    value: 'on_leave',
    label: 'On Leave',
    color: 'info',
  },
];

const getStatusMeta = (status) => {
  const match = STATUS_OPTIONS.find(
    (s) => s.value === status
  );

  if (match) {
    return {
      label: match.label,
      color: match.color,
      icon:
        status === 'present' ? (
          <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
        ) : status === 'absent' ? (
          <HighlightOffIcon sx={{ fontSize: 14 }} />
        ) : undefined,
    };
  }

  return {
    label: status || 'Unknown',
    color: 'default',
    icon: undefined,
  };
};

const Attendance = () => {
  const theme = useTheme();
  const labelSx = {
  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
  '& .MuiInputBase-input': { color: theme.palette.text.primary },
  '& .MuiInputBase-input::placeholder': {
    color: theme.palette.text.secondary,
    opacity: 1,
  },
  '& .MuiSelect-icon': { color: theme.palette.text.primary },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    color: theme.palette.text.secondary,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.text.primary,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '& input::-webkit-calendar-picker-indicator': {
    filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
  },
};
  const navigate = useNavigate();

  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  const [students, setStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const filtersOpen = Boolean(filterAnchorEl);

  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [courses, setCourses] = useState([]);
  const [filteredBatches, setFilteredBatches] = useState([]);

    const { selectedBranchId } = useBranch();

  const [organizationId, setOrganizationId] =
    useState(null);

  const [attendanceMap, setAttendanceMap] =
    useState({});

  const [showUserGuideModal, setShowUserGuideModal] =
    useState(false);

  const [openSnackbar, setOpenSnackbar] =
    useState(false);

  const [snackbarMessage, setSnackbarMessage] =
    useState('');

  const [snackbarSeverity, setSnackbarSeverity] =
    useState('success');

  const [loading, setLoading] = useState(false);

  const [viewAttendanceModal, setViewAttendanceModal] =
    useState(false);

  const [selectedStudent, setSelectedStudent] =
    useState(null);

  const [
    studentAttendanceHistory,
    setStudentAttendanceHistory,
  ] = useState([]);

  const [attendanceLoading, setAttendanceLoading] =
    useState(false);

  const [historySummary, setHistorySummary] =
    useState(null);

  const [detailModalOpen, setDetailModalOpen] =
    useState(false);

  const [detailRecord, setDetailRecord] =
    useState(null);

  const [detailLoading, setDetailLoading] =
    useState(false);

  const [historyFromDate, setHistoryFromDate] =
    useState('');

  const [historyToDate, setHistoryToDate] =
    useState('');

  const [summaryModalOpen, setSummaryModalOpen] =
    useState(false);

  const [summaryLoading, setSummaryLoading] =
    useState(false);

  const [summaryStudent, setSummaryStudent] =
    useState(null);

  const [summaryData, setSummaryData] =
    useState(null);

  const showNotification = useCallback(
    (message, severity = 'success') => {
      setSnackbarMessage(message);
      setSnackbarSeverity(severity);
      setOpenSnackbar(true);
    },
    []
  );

  const getAuthToken = () => {
    return (
      localStorage.getItem('accessToken') ||
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('access_token') ||
      sessionStorage.getItem('accessToken') ||
      sessionStorage.getItem('token')
    );
  };

  const getOrganizationId = () => {
    try {
      const user =
        JSON.parse(
          sessionStorage.getItem('user')
        ) || {};

      if (user?.organization_id) {
        return user.organization_id;
      }
    } catch {}

    try {
      const clientData =
        JSON.parse(
          localStorage.getItem('client')
        ) || {};

      if (clientData?.id) {
        return clientData.id;
      }
    } catch {}

    return null;
  };

  /* =========================================================
     ORGANIZATION
     ========================================================= */

  useEffect(() => {
    const orgId = getOrganizationId();

    if (orgId) {
      setOrganizationId(orgId);
    }
  }, []);


  /* =========================================================
     COURSES
     IMPORTANT:
     Selected Branch -> branch_id is sent to Course API.
     Therefore only courses belonging to selected branch
     should be returned.
     ========================================================= */

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const accessToken = getAuthToken();

        const params = {};

        if (organizationId) {
          params.organization_id =
            organizationId;
        }

        if (selectedBranchId) {
          params.branch_id =
            selectedBranchId;
        }

        console.log(
          'COURSE FILTER PARAMS:',
          params
        );

        const response = await axios.get(
          `${API_BASE_URL}/api/course/courses/`,
          {
            params,
            headers: {
              Authorization:
                `Bearer ${accessToken}`,
            },
          }
        );

        const coursesData =
          response.data?.data ??
          response.data?.results ??
          response.data?.courses ??
          response.data ??
          [];

        const courseArray =
          Array.isArray(coursesData)
            ? coursesData
            : [];

        /*
         * Extra frontend safety filter.
         * If API returns branch information inside course,
         * remove courses belonging to another branch.
         */
        const branchFilteredCourses =
          selectedBranchId
            ? courseArray.filter((course) => {
                const courseBranchId =
                  course?.branch?.id ??
                  course?.branch_id ??
                  course?.branch;

                /*
                 * If course response does not contain branch
                 * information, keep it because the API request
                 * itself is already branch-filtered.
                 */
                if (
                  courseBranchId ===
                    undefined ||
                  courseBranchId === null ||
                  courseBranchId === ''
                ) {
                  return true;
                }

                return (
                  String(courseBranchId) ===
                  String(selectedBranchId)
                );
              })
            : courseArray;

        console.log(
          'COURSES AFTER BRANCH FILTER:',
          branchFilteredCourses
        );

        setCourses(
          branchFilteredCourses
        );
      } catch (error) {
        console.error(
          'Failed to fetch courses:',
          error
        );

        setCourses([]);
      }
    };

    fetchCourses();
  }, [
    organizationId,
    selectedBranchId,
  ]);

  /* =========================================================
     BATCHES
     ========================================================= */

  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const accessToken = getAuthToken();

        let batchesData = [];

        if (selectedCourse) {
          const response =
            await axios.get(
              `${API_BASE_URL}/api/course/courses/${selectedCourse}/batches/`,
              {
                params: selectedBranchId
                  ? {
                      branch_id:
                        selectedBranchId,
                    }
                  : {},
                headers: {
                  Authorization:
                    `Bearer ${accessToken}`,
                },
              }
            );

          batchesData =
            response.data?.batches ||
            response.data?.results ||
            response.data?.data ||
            response.data ||
            [];
        } else {
          const response =
            await axios.get(
              `${API_BASE_URL}/api/course/batches/`,
              {
                params: selectedBranchId
                  ? {
                      branch_id:
                        selectedBranchId,
                    }
                  : {},
                headers: {
                  Authorization:
                    `Bearer ${accessToken}`,
                },
              }
            );

          batchesData =
            response.data?.results ||
            response.data?.data ||
            response.data ||
            [];
        }

        const batchArray =
          Array.isArray(batchesData)
            ? batchesData
            : [];

        const branchFilteredBatches =
          selectedBranchId
            ? batchArray.filter((batch) => {
                const batchBranchId =
                  batch?.branch?.id ??
                  batch?.branch_id ??
                  batch?.branch;

                if (
                  batchBranchId ===
                    undefined ||
                  batchBranchId === null ||
                  batchBranchId === ''
                ) {
                  return true;
                }

                return (
                  String(batchBranchId) ===
                  String(selectedBranchId)
                );
              })
            : batchArray;

        setFilteredBatches(
          branchFilteredBatches
        );

        if (
          selectedBatch &&
          !branchFilteredBatches.some(
            (batch) =>
              String(batch.id) ===
              String(selectedBatch)
          )
        ) {
          setSelectedBatch('');
        }
      } catch (error) {
        console.error(
          'Failed to fetch batches:',
          error
        );

        setFilteredBatches([]);
      }
    };

    fetchBatches();
  }, [
    selectedCourse,
    selectedBranchId,
  ]);

  /* =========================================================
     FETCH STUDENTS
     ========================================================= */

  useEffect(() => {
    const fetchStudents = async () => {
      setLoading(true);

      try {
        const accessToken =
          getAuthToken();

        let url =
          `${API_BASE_URL}/api/admission/admissions/`;

        if (
          selectedCourse &&
          selectedBatch
        ) {
          url =
            `${API_BASE_URL}/api/admission/admission/course-batch/?course_id=${encodeURIComponent(
              selectedCourse
            )}&batch_id=${encodeURIComponent(
              selectedBatch
            )}`;

          if (selectedBranchId) {
            url +=
              `&branch_id=${encodeURIComponent(
                selectedBranchId
              )}`;
          }
        } else if (
          selectedCourse &&
          !selectedBatch
        ) {
          url =
            `${API_BASE_URL}/api/admission/admission/course-batch/?course_id=${encodeURIComponent(
              selectedCourse
            )}`;

          if (selectedBranchId) {
            url +=
              `&branch_id=${encodeURIComponent(
                selectedBranchId
              )}`;
          }
        } else if (
          selectedBatch &&
          !selectedCourse
        ) {
          url =
            `${API_BASE_URL}/api/admission/admission/course-batch/?batch_id=${encodeURIComponent(
              selectedBatch
            )}`;

          if (selectedBranchId) {
            url +=
              `&branch_id=${encodeURIComponent(
                selectedBranchId
              )}`;
          }
        } else {
          const params = [];

          if (selectedBranchId) {
            params.push(
              `branch_id=${encodeURIComponent(
                selectedBranchId
              )}`
            );
          }

          if (params.length) {
            url += `?${params.join('&')}`;
          }
        }

        console.log(
          'STUDENT API URL:',
          url
        );

        const response =
          await axios.get(url, {
            headers: {
              Authorization:
                `Bearer ${accessToken}`,
            },
          });

        const admissionsData =
          response.data?.results ||
          response.data?.data ||
          response.data ||
          [];

        const admissionArray =
          Array.isArray(admissionsData)
            ? admissionsData
            : [];

        const mappedStudents =
          admissionArray.map(
            (admission) => {
              const courseNames =
                admission.courses
                  ?.map(
                    (c) =>
                      c.course_name
                  )
                  .join(', ') ||
                '-';

              const allBatchNames = [];

              admission.courses?.forEach(
                (course) => {
                  course.batches?.forEach(
                    (batch) => {
                      if (
                        batch.batch_name &&
                        !allBatchNames.includes(
                          batch.batch_name
                        )
                      ) {
                        allBatchNames.push(
                          batch.batch_name
                        );
                      }
                    }
                  );
                }
              );

              return {
                id: admission.id,
                AdmissionNo:
                  admission.admission_code,
                Name:
                  admission.candidate_name,
                course_name:
                  courseNames,
                batch_name:
                  allBatchNames.join(
                    ', '
                  ) || '-',
              };
            }
          );

        setStudents(
          mappedStudents
        );
      } catch (error) {
        console.error(
          'Failed to fetch admissions:',
          error
        );

        showNotification(
          'Failed to fetch students',
          'error'
        );

        setStudents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, [
    selectedCourse,
    selectedBatch,
    selectedBranchId,
    showNotification,
  ]);

  /* =========================================================
     ATTENDANCE FOR SELECTED DATE
     ========================================================= */

  useEffect(() => {
    if (!selectedDate) {
      return;
    }

    const fetchAttendance =
      async () => {
        try {
          const accessToken =
            getAuthToken();

          const orgId =
            getOrganizationId();

          const params = [
            `start_date=${encodeURIComponent(
              selectedDate
            )}`,
            `end_date=${encodeURIComponent(
              selectedDate
            )}`,
          ];

          if (orgId) {
            params.push(
              `organization=${encodeURIComponent(
                orgId
              )}`
            );
          }

          if (selectedBranchId) {
            params.push(
              `branch=${encodeURIComponent(
                selectedBranchId
              )}`
            );
          }

          const response =
            await axios.get(
              `${API_BASE_URL}/api/admission/attendance/?${params.join(
                '&'
              )}`,
              {
                headers: {
                  Authorization:
                    `Bearer ${accessToken}`,
                },
              }
            );

          const records =
            Array.isArray(
              response.data?.results
            )
              ? response.data.results
              : Array.isArray(
                  response.data
                )
              ? response.data
              : [];

          const map = {};

          records.forEach(
            (item) => {
              map[item.admission] = {
                status: item.status,
                recordId: item.id,
                time_in:
                  item.time_in,
                time_out:
                  item.time_out,
                remark:
                  item.remark,
              };
            }
          );

          setAttendanceMap(map);
        } catch (error) {
          console.error(
            'Failed to fetch attendance:',
            error
          );

          showNotification(
            error.response?.data
              ?.detail ||
              error.response?.data
                ?.message ||
              'Failed to fetch attendance',
            'error'
          );
        }
      };

    fetchAttendance();
  }, [
    selectedDate,
    selectedBranchId,
    showNotification,
  ]);

  /* =========================================================
     NAVIGATION
     ========================================================= */

  const handleGoToAddAttendance =
    () => {
      navigate(
        '/add-student-attendance'
      );
    };

  const handleGoToEditAttendance =
    (recordId) => {
      navigate(
        `/edit-student-attendance/${recordId}`
      );
    };

  /* =========================================================
     ATTENDANCE DETAIL
     ========================================================= */

  const fetchAttendanceDetail =
    async (recordId) => {
      setDetailLoading(true);
      setDetailModalOpen(true);

      try {
        const accessToken =
          getAuthToken();

        const response =
          await axios.get(
            `${API_BASE_URL}/api/admission/attendance/${recordId}/`,
            {
              headers: {
                Authorization:
                  `Bearer ${accessToken}`,
              },
            }
          );

        setDetailRecord(
          response.data
        );
      } catch (error) {
        console.error(
          'Failed to fetch attendance detail:',
          error
        );

        showNotification(
          'Failed to fetch attendance record detail',
          'error'
        );

        setDetailModalOpen(false);
      } finally {
        setDetailLoading(false);
      }
    };

  const handleCloseDetailModal =
    () => {
      setDetailModalOpen(false);
      setDetailRecord(null);
    };

  /* =========================================================
     CURRENT USER
     ========================================================= */

  const safeParseJSON = (
    str,
    fallback = {}
  ) => {
    try {
      return JSON.parse(str) || fallback;
    } catch {
      return fallback;
    }
  };

  const currentUser =
    safeParseJSON(
      sessionStorage.getItem('user')
    );

  const role =
    currentUser?.role;

  const admissionId =
    currentUser?.admission_details
      ?.admission_id;

  /* =========================================================
     STUDENT HISTORY
     ========================================================= */

  const fetchStudentAttendance =
    async (
      studentId,
      studentName,
      admissionCode,
      fromDate = historyFromDate,
      toDate = historyToDate,
      shouldOpenModal = true
    ) => {
      setAttendanceLoading(true);

      try {
        const accessToken =
          getAuthToken();

        const params = [];

        if (fromDate) {
          params.push(
            `start_date=${encodeURIComponent(
              fromDate
            )}`
          );
        }

        if (toDate) {
          params.push(
            `end_date=${encodeURIComponent(
              toDate
            )}`
          );
        }

        const query = params.length
          ? `?${params.join('&')}`
          : '';

        const response =
          await axios.get(
            `${API_BASE_URL}/api/admission/student-attendance/${studentId}/${query}`,
            {
              headers: {
                Authorization:
                  `Bearer ${accessToken}`,
              },
            }
          );

        const data =
          response.data || {};

        const records =
          Array.isArray(data.history)
            ? data.history
            : [];

        const sortedAttendance =
          [...records].sort(
            (a, b) =>
              new Date(b.date) -
              new Date(a.date)
          );

        setStudentAttendanceHistory(
          sortedAttendance
        );

        const presentDays =
          data.summary?.present_days ??
          sortedAttendance.filter(
            (r) =>
              r.status ===
              'present'
          ).length;

        const absentDays =
          data.summary?.absent_days ??
          sortedAttendance.filter(
            (r) =>
              r.status ===
              'absent'
          ).length;

        const halfDays =
          data.summary?.half_days ??
          sortedAttendance.filter(
            (r) =>
              r.status ===
              'half_day'
          ).length;

        const leaveDays =
          data.summary?.leave_days ??
          sortedAttendance.filter(
            (r) =>
              r.status ===
              'on_leave'
          ).length;

        const workingDays =
          data.summary?.working_days ??
          sortedAttendance.length;

        const attendancePercentage =
          data.summary
            ?.attendance_percentage ??
          (workingDays > 0
            ? Math.round(
                (presentDays /
                  workingDays) *
                  100
              )
            : 0);

        setHistorySummary({
          workingDays,
          presentDays,
          absentDays,
          halfDays,
          leaveDays,
          attendancePercentage,
        });

        setSelectedStudent({
          id:
            data.student?.id ??
            studentId,
          name:
            data.student
              ?.candidate_name ||
            studentName,
          admissionCode:
            data.student
              ?.admission_code ||
            admissionCode,
          mobileNo:
            data.student?.mobile_no,
          email:
            data.student?.email,
        });

        if (shouldOpenModal) {
          setViewAttendanceModal(
            true
          );
        }
      } catch (error) {
        console.error(
          'Error fetching student attendance:',
          error
        );

        showNotification(
          'Failed to fetch attendance history',
          'error'
        );
      } finally {
        setAttendanceLoading(false);
      }
    };

  useEffect(() => {
    if (
      role === 'student' &&
      admissionId
    ) {
      fetchStudentAttendance(
        admissionId,
        currentUser
          ?.admission_details
          ?.student_name,
        currentUser
          ?.admission_details
          ?.admission_no,
        historyFromDate,
        historyToDate,
        false
      );
    }
  }, [
    role,
    admissionId,
  ]);

  const handleHistoryDateFilter =
    () => {
      if (!selectedStudent) {
        return;
      }

      fetchStudentAttendance(
        selectedStudent.id,
        selectedStudent.name,
        selectedStudent.admissionCode,
        historyFromDate,
        historyToDate
      );
    };

  const handleCloseHistoryModal =
    () => {
      setViewAttendanceModal(
        false
      );
      setHistoryFromDate('');
      setHistoryToDate('');
      setHistorySummary(null);
    };

  /* =========================================================
     ATTENDANCE SUMMARY
     ========================================================= */

  const fetchAttendanceSummary =
    async (
      studentId,
      studentName,
      admissionCode,
      shouldOpenModal = true
    ) => {
      setSummaryLoading(true);

      if (shouldOpenModal) {
        setSummaryModalOpen(true);
      }

      try {
        const accessToken =
          getAuthToken();

        const response =
          await axios.get(
            `${API_BASE_URL}/api/admission/student-attendance/${studentId}/`,
            {
              headers: {
                Authorization:
                  `Bearer ${accessToken}`,
              },
            }
          );

        const data =
          response.data || {};

        setSummaryStudent({
          name:
            data.student
              ?.candidate_name ||
            studentName,
          admissionCode:
            data.student
              ?.admission_code ||
            admissionCode,
        });

        setSummaryData({
          workingDays:
            data.summary
              ?.working_days ?? 0,
          presentDays:
            data.summary
              ?.present_days ?? 0,
          absentDays:
            data.summary
              ?.absent_days ?? 0,
          halfDays:
            data.summary
              ?.half_days ?? 0,
          leaveDays:
            data.summary
              ?.leave_days ?? 0,
          attendancePercentage:
            data.summary
              ?.attendance_percentage ??
            0,
        });
      } catch (error) {
        console.error(
          'Failed to fetch attendance summary:',
          error
        );

        showNotification(
          'Failed to fetch attendance summary',
          'error'
        );

        setSummaryModalOpen(
          false
        );
      } finally {
        setSummaryLoading(false);
      }
    };

  useEffect(() => {
    if (
      role === 'student' &&
      admissionId
    ) {
      fetchAttendanceSummary(
        admissionId,
        currentUser
          ?.admission_details
          ?.student_name,
        currentUser
          ?.admission_details
          ?.admission_no,
        false
      );
    }
  }, [
    role,
    admissionId,
  ]);

  const handleCloseSummaryModal =
    () => {
      setSummaryModalOpen(false);
      setSummaryStudent(null);
      setSummaryData(null);
    };

  /* =========================================================
     FILTER HANDLERS
     ========================================================= */

   useEffect(() => {
    setSelectedCourse('');
    setSelectedBatch('');
    setFilteredBatches([]);
  }, [selectedBranchId]);

  const handleCourseChange =
    (e) => {
      setSelectedCourse(
        e.target.value
      );

      setSelectedBatch('');
    };

  const handleBatchChange =
    (e) => {
      setSelectedBatch(
        e.target.value
      );
    };

  const filteredStudents =
    students.filter(
      (student) => {
        if (
          currentUser?.role ===
          'student'
        ) {
          return (
            Number(student.id) ===
            Number(
              currentUser
                ?.admission_details
                ?.admission_id
            )
          );
        }

        const query =
          searchQuery
            .trim()
            .toLowerCase();

        if (!query) {
          return true;
        }

        return (
          (
            student.AdmissionNo ||
            ''
          )
            .toLowerCase()
            .includes(query) ||
          (
            student.Name || ''
          )
            .toLowerCase()
            .includes(query)
        );
      }
    );

  return (
    <Container
      sx={{
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      {['teacher', 'admin'].includes(
        currentUser?.role
      ) && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent:
                'space-between',
              mb: 2,
              pt: 1,
            }}
          >
        
           {/* SEARCH */}
            <TextField
              sx={{
                width: { xs: '100%', sm: 280 },
                '& .MuiInputLabel-root': {
                  color: theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
                '& .MuiInputBase-input': {
                  color: theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.primary,
                },
                '& .MuiInputBase-input::placeholder': {
                  color: theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.disabled,
                  opacity: 1,
                },
                '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                  color: theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.disabled,
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: theme.palette.divider },
                  '&:hover fieldset': { borderColor: theme.palette.primary.main },
                  '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
                },
              }}
              placeholder="Search by name or admission no"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Box Gap={1} sx={{ display: 'flex', alignItems: 'center', gap: 1 }} >
              <Button
                variant="contained"
                startIcon={
                  <AddCircleOutlineIcon />
                }
                onClick={
                  handleGoToAddAttendance
                }
                sx={{
                  textTransform:
                    'none',
                  fontWeight: 600,
                  borderRadius: '8px',
                }}
              >
               + Attendance
              </Button>
   {/* FILTER BUTTON */}
            <Button
              variant="contained"
              startIcon={<FilterListIcon />}
              onClick={(event) => setFilterAnchorEl(event.currentTarget)}
              sx={{
                height: 40,
                px: 2,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              Filter
            </Button>
            
            <Popover
              open={filtersOpen}
              anchorEl={filterAnchorEl}
              onClose={() => setFilterAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  mt: 1,
                  p: 2,
                  width: { xs: 'calc(100vw - 32px)', sm: 320 },
                  maxWidth: 'calc(100vw - 32px)',
                  borderRadius: 3,
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: theme.shadows[8],
                },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Filter Attendance
                </Typography>

                <TextField
                  fullWidth
                  size="small"
                  label="Attendance Date"
                  type="date"
                  value={selectedDate}
                  onChange={(event) => setSelectedDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={labelSx}
                />

                <FormControl fullWidth size="small" sx={labelSx}>
                  <InputLabel>Course</InputLabel>
                  <Select
                    value={selectedCourse}
                    onChange={handleCourseChange}
                    label="Course"
                  >
                    <MenuItem value=""><em>All Courses</em></MenuItem>
                    {courses.map((course) => (
                      <MenuItem key={course.id} value={String(course.id)}>
                        {course.course_name || course.name}
                      </MenuItem>
                    ))}
                    {selectedBranchId && courses.length === 0 && (
                      <MenuItem disabled>No courses available for selected branch</MenuItem>
                    )}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small" sx={labelSx}>
                  <InputLabel>Batch</InputLabel>
                  <Select
                    value={selectedBatch}
                    onChange={handleBatchChange}
                    label="Batch"
                  >
                    <MenuItem value=""><em>All Batches</em></MenuItem>
                    {filteredBatches.length > 0 ? (
                      filteredBatches.map((batch) => (
                        <MenuItem key={batch.id} value={String(batch.id)}>
                          {batch.batch_name || batch.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        {selectedCourse
                          ? 'No batches available for this course'
                          : 'No batches available'}
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedDate(new Date().toISOString().split('T')[0]);
                      setSelectedCourse('');
                      setSelectedBatch('');
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => setFilterAnchorEl(null)}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            </Popover>
              <IconButton
                onClick={() =>
                  setShowUserGuideModal(
                    true
                  )
                }
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                }}
              >
                <InfoOutlineIcon
                  sx={{ fontSize: 26 }}
                />
              </IconButton>
</Box>
            </Box>


      
           

         

        
        </>
      )}

      {/* =========================================================
          STUDENT TABLE
          ========================================================= */}

      <Paper
        elevation={2}
        sx={{
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto',
          backgroundColor:
            theme.palette
              .background.paper,
          borderRadius: '16px',
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent:
                'center',
              p: 6,
            }}
          >
            <CircularProgress />
          </Box>
        ) : filteredStudents.length >
          0 ? (
          <Box
            sx={{
              width: '100%',
              overflowX: 'auto',
            }}
          >
            <TableContainer>
              <Table
                sx={{
                  '& .MuiTableHead-root .MuiTableCell-root':
                    {
                      fontWeight: 700,
                      backgroundColor:
                        theme.palette
                          .primary
                          .main,
                      color: '#fff',
                      fontSize:
                        '0.85rem',
                      letterSpacing:
                        '0.05em',
                      textTransform:
                        'uppercase',
                      py: 1.8,
                    },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        width: 60,
                      }}
                    >
                      S.No
                    </TableCell>
                    <TableCell>
                      Admission No
                    </TableCell>
                    <TableCell>
                      Candidate Name
                    </TableCell>
                    <TableCell>
                      Course
                    </TableCell>
                    <TableCell>
                      Batch
                    </TableCell>
                    <TableCell
                      sx={{
                        width: 150,
                        textAlign:
                          'center',
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredStudents.map(
                    (
                      student,
                      index
                    ) => {
                      const record =
                        attendanceMap[
                          student.id
                        ];

                      const hasExistingRecord =
                        !!record;

                      return (
                        <TableRow
                          key={
                            student.id
                          }
                          sx={{
                            backgroundColor:
                              index %
                                2 !==
                              0
                                ? theme
                                    .palette
                                    .mode ===
                                  'dark'
                                  ? 'rgba(255,255,255,0.02)'
                                  : 'rgba(0,0,0,0.015)'
                                : theme
                                    .palette
                                    .background
                                    .paper,
                            '&:hover':
                              {
                                backgroundColor:
                                  theme
                                    .palette
                                    .mode ===
                                  'dark'
                                    ? 'rgba(255,255,255,0.05)'
                                    : 'rgba(0,0,0,0.04)',
                              },
                          }}
                        >
                          <TableCell>
                            {index + 1}
                          </TableCell>

                          <TableCell
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            {
                              student.AdmissionNo
                            }
                          </TableCell>

                          <TableCell>
                            {
                              student.Name
                            }
                          </TableCell>

                          <TableCell>
                            {
                              student.course_name
                            }
                          </TableCell>

                          <TableCell>
                            {
                              student.batch_name
                            }
                          </TableCell>

                          <TableCell align="center">
                            <Box
                              sx={{
                                display:
                                  'flex',
                                justifyContent:
                                  'center',
                                gap: 0.5,
                              }}
                            >
                              <Tooltip title="View Attendance History">
                                <IconButton
                                  onClick={() =>
                                    fetchStudentAttendance(
                                      student.id,
                                      student.Name,
                                      student.AdmissionNo
                                    )
                                  }
                                  size="small"
                                  sx={{
                                    color:
                                      theme
                                        .palette
                                        .info
                                        .main,
                                  }}
                                >
                                  <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <path
                                      d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
                                      fill="currentColor"
                                    />
                                  </svg>
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="View Attendance Summary">
                                <IconButton
                                  onClick={() =>
                                    fetchAttendanceSummary(
                                      student.id,
                                      student.Name,
                                      student.AdmissionNo
                                    )
                                  }
                                  size="small"
                                  sx={{
                                    color:
                                      theme
                                        .palette
                                        .secondary
                                        ?.main ||
                                      theme
                                        .palette
                                        .primary
                                        .main,
                                  }}
                                >
                                  <BarChartIcon
                                    sx={{
                                      fontSize: 20,
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>

                              {hasExistingRecord && (
                                <Tooltip title="Edit Attendance">
                                  <IconButton
                                    onClick={() =>
                                      handleGoToEditAttendance(
                                        record.recordId
                                      )
                                    }
                                    size="small"
                                    sx={{
                                      color:
                                        theme
                                          .palette
                                          .primary
                                          .main,
                                    }}
                                  >
                                    <EditOutlinedIcon
                                      sx={{
                                        fontSize: 20,
                                      }}
                                    />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    }
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent:
                'center',
              p: 6,
            }}
          >
            <Typography>
              No students found
            </Typography>
          </Box>
        )}
      </Paper>

      {/* =========================================================
          SNACKBAR
          ========================================================= */}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() =>
          setOpenSnackbar(false)
        }
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert
          onClose={() =>
            setOpenSnackbar(false)
          }
          severity={
            snackbarSeverity
          }
          sx={{
            width: '100%',
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* =========================================================
          HISTORY MODAL
          ========================================================= */}

      <Modal
        open={
          viewAttendanceModal
        }
        onClose={
          handleCloseHistoryModal
        }
      >
        <Box
          sx={{
            position:
              'absolute',
            top: '50%',
            left: '50%',
            transform:
              'translate(-50%, -50%)',
            width: {
              xs: '90%',
              sm: 800,
              md: 950,
            },
            maxHeight: '85vh',
            bgcolor:
              'background.paper',
            borderRadius:
              '16px',
            boxShadow: 24,
            overflow: 'hidden',
            display: 'flex',
            flexDirection:
              'column',
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette
                  .primary.main,
              p: 2,
              display: 'flex',
              justifyContent:
                'space-between',
              alignItems:
                'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight: 700,
              }}
            >
              Attendance History
            </Typography>

            <IconButton
              onClick={
                handleCloseHistoryModal
              }
              sx={{
                color: '#fff',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {selectedStudent &&
            !attendanceLoading && (
              <Box
                sx={{
                  p: 3,
                  borderBottom:
                    `1px solid ${theme.palette.divider}`,
                }}
              >
                <Grid
                  container
                  spacing={2}
                >
                  <Grid
                    item
                    xs={12}
                    sm={6}
                  >
                    <Typography
                      variant="body2"
                    >
                      Student Name
                    </Typography>
                    <Typography
                      fontWeight={600}
                    >
                      {
                        selectedStudent.name
                      }
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                  >
                    <Typography
                      variant="body2"
                    >
                      Admission Code
                    </Typography>
                    <Typography
                      fontWeight={600}
                    >
                      {
                        selectedStudent.admissionCode
                      }
                    </Typography>
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap:
                      'wrap',
                    gap: 2,
                    alignItems:
                      'center',
                    mt: 2.5,
                  }}
                >
                  <TextField
                  sx={labelSx}
                    label="From"
                    type="date"
                    size="small"
                    value={
                      historyFromDate
                    }
                    onChange={(e) =>
                      setHistoryFromDate(
                        e.target.value
                      )
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />

                  <TextField
                  sx={labelSx}
                    label="To"
                    type="date"
                    size="small"
                    value={
                      historyToDate
                    }
                    onChange={(e) =>
                      setHistoryToDate(
                        e.target.value
                      )
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={
                      handleHistoryDateFilter
                    }
                    sx={{
                      textTransform:
                        'none',
                    }}
                  >
                    Apply Filter
                  </Button>

                  {(historyFromDate ||
                    historyToDate) && (
                    <Button
                      size="small"
                      onClick={() => {
                        setHistoryFromDate(
                          ''
                        );
                        setHistoryToDate(
                          ''
                        );

                        fetchStudentAttendance(
                          selectedStudent.id,
                          selectedStudent.name,
                          selectedStudent.admissionCode,
                          '',
                          ''
                        );
                      }}
                      sx={{
                        textTransform:
                          'none',
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </Box>
              </Box>
            )}

          <CustomTableScrollbar
            sx={{
              flex: 1,
              overflowY:
                'auto',
              p: 2.5,
            }}
          >
            {attendanceLoading ? (
              <Box
                sx={{
                  display:
                    'flex',
                  justifyContent:
                    'center',
                  py: 8,
                }}
              >
                <CircularProgress />
              </Box>
            ) : studentAttendanceHistory.length ===
              0 ? (
              <Box
                sx={{
                  textAlign:
                    'center',
                  py: 8,
                }}
              >
                <Typography>
                  No attendance records
                  found for this
                  student
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer
                  component={
                    Paper
                  }
                  elevation={0}
                >
                  <Table>
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor:
                            theme
                              .palette
                              .primary
                              .main,
                        }}
                      >
                        <TableCell
                          sx={{
                            color:
                              '#fff',
                            fontWeight:
                              700,
                          }}
                        >
                          S.No
                        </TableCell>
                        <TableCell
                          sx={{
                            color:
                              '#fff',
                            fontWeight:
                              700,
                          }}
                        >
                          Date
                        </TableCell>
                        <TableCell
                          sx={{
                            color:
                              '#fff',
                            fontWeight:
                              700,
                          }}
                        >
                          Day
                        </TableCell>
                        <TableCell
                          sx={{
                            color:
                              '#fff',
                            fontWeight:
                              700,
                          }}
                        >
                          Status
                        </TableCell>
                        <TableCell
                          sx={{
                            color:
                              '#fff',
                            fontWeight:
                              700,
                          }}
                        >
                          Time In
                        </TableCell>
                        <TableCell
                          sx={{
                            color:
                              '#fff',
                            fontWeight:
                              700,
                          }}
                        >
                          Time Out
                        </TableCell>
                        <TableCell
                          sx={{
                            color:
                              '#fff',
                            fontWeight:
                              700,
                          }}
                        >
                          Total Hours
                        </TableCell>
                        <TableCell
                          sx={{
                            color:
                              '#fff',
                            fontWeight:
                              700,
                          }}
                        >
                          Remark
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            color:
                              '#fff',
                            fontWeight:
                              700,
                          }}
                        >
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {studentAttendanceHistory.map(
                        (
                          record,
                          idx
                        ) => {
                          const recordDate =
                            new Date(
                              record.date
                            );

                          const dayName =
                            recordDate.toLocaleDateString(
                              'en-US',
                              {
                                weekday:
                                  'long',
                              }
                            );

                          const statusMeta =
                            getStatusMeta(
                              record.status
                            );

                          return (
                            <TableRow
                              key={
                                record.id ??
                                idx
                              }
                            >
                              <TableCell>
                                {idx +
                                  1}
                              </TableCell>

                              <TableCell>
                                {recordDate.toLocaleDateString(
                                  'en-US',
                                  {
                                    year: 'numeric',
                                    month:
                                      'long',
                                    day: 'numeric',
                                  }
                                )}
                              </TableCell>

                              <TableCell>
                                {
                                  dayName
                                }
                              </TableCell>

                              <TableCell>
                                <Chip
                                  icon={
                                    statusMeta.icon
                                  }
                                  label={
                                    statusMeta.label
                                  }
                                  size="small"
                                  color={
                                    statusMeta.color
                                  }
                                />
                              </TableCell>

                              <TableCell>
                                {
                                  record.time_in ||
                                  '—'
                                }
                              </TableCell>

                              <TableCell>
                                {
                                  record.time_out ||
                                  '—'
                                }
                              </TableCell>

                              <TableCell>
                                {record.total_hours ??
                                  '—'}
                              </TableCell>

                              <TableCell>
                                {
                                  record.remark ||
                                  '—'
                                }
                              </TableCell>

                              <TableCell align="center">
                                <Tooltip title="Edit Attendance">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleGoToEditAttendance(
                                        record.id
                                      )
                                    }
                                  >
                                    <EditOutlinedIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        }
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius:
                      '12px',
                    backgroundColor:
                      theme.palette
                        .mode ===
                      'dark'
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(0,0,0,0.03)',
                    display:
                      'flex',
                    justifyContent:
                      'space-around',
                    flexWrap:
                      'wrap',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      textAlign:
                        'center',
                    }}
                  >
                    <Typography
                      variant="h4"
                      fontWeight={700}
                    >
                      {historySummary?.workingDays ??
                        studentAttendanceHistory.length}
                    </Typography>
                    <Typography variant="caption">
                      Working Days
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      textAlign:
                        'center',
                    }}
                  >
                    <Typography
                      variant="h4"
                      color="success.main"
                      fontWeight={700}
                    >
                      {historySummary?.presentDays ??
                        0}
                    </Typography>
                    <Typography variant="caption">
                      Total Present
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      textAlign:
                        'center',
                    }}
                  >
                    <Typography
                      variant="h4"
                      color="error.main"
                      fontWeight={700}
                    >
                      {historySummary?.absentDays ??
                        0}
                    </Typography>
                    <Typography variant="caption">
                      Total Absent
                    </Typography>
                  </Box>

                  {(historySummary?.halfDays ??
                    0) >
                    0 && (
                    <Box
                      sx={{
                        textAlign:
                          'center',
                      }}
                    >
                      <Typography
                        variant="h4"
                        color="warning.main"
                        fontWeight={
                          700
                        }
                      >
                        {
                          historySummary.halfDays
                        }
                      </Typography>
                      <Typography variant="caption">
                        Half Days
                      </Typography>
                    </Box>
                  )}

                  {(historySummary?.leaveDays ??
                    0) >
                    0 && (
                    <Box
                      sx={{
                        textAlign:
                          'center',
                      }}
                    >
                      <Typography
                        variant="h4"
                        color="info.main"
                        fontWeight={
                          700
                        }
                      >
                        {
                          historySummary.leaveDays
                        }
                      </Typography>
                      <Typography variant="caption">
                        Leave Days
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      textAlign:
                        'center',
                    }}
                  >
                    <Typography
                      variant="h4"
                      color="primary.main"
                      fontWeight={700}
                    >
                      {historySummary?.attendancePercentage ??
                        0}
                      %
                    </Typography>
                    <Typography variant="caption">
                      Attendance Rate
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </CustomTableScrollbar>
        </Box>
      </Modal>

      {/* =========================================================
          DETAIL MODAL
          ========================================================= */}

      <Modal
        open={
          detailModalOpen
        }
        onClose={
          handleCloseDetailModal
        }
      >
        <Box
          sx={{
            position:
              'absolute',
            top: '50%',
            left: '50%',
            transform:
              'translate(-50%, -50%)',
            width: {
              xs: '90%',
              sm: 500,
            },
            maxHeight: '85vh',
            bgcolor:
              'background.paper',
            borderRadius:
              '16px',
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette
                  .primary.main,
              p: 2,
              display: 'flex',
              justifyContent:
                'space-between',
              alignItems:
                'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight: 700,
              }}
            >
              Attendance Record{' '}
              {detailRecord?.id
                ? `#${detailRecord.id}`
                : ''}
            </Typography>

            <IconButton
              onClick={
                handleCloseDetailModal
              }
              sx={{
                color: '#fff',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <CustomTableScrollbar
            sx={{
              maxHeight:
                '70vh',
              overflowY:
                'auto',
              p: 3,
            }}
          >
            {detailLoading ? (
              <Box
                sx={{
                  display:
                    'flex',
                  justifyContent:
                    'center',
                  py: 6,
                }}
              >
                <CircularProgress />
              </Box>
            ) : detailRecord ? (
              <Grid
                container
                spacing={2.5}
              >
                <Grid
                  item
                  xs={6}
                >
                  <Typography variant="body2">
                    Record ID
                  </Typography>
                  <Typography fontWeight={600}>
                    {
                      detailRecord.id
                    }
                  </Typography>
                </Grid>

                <Grid
                  item
                  xs={6}
                >
                  <Typography variant="body2">
                    Admission ID
                  </Typography>
                  <Typography fontWeight={600}>
                    {
                      detailRecord.admission
                    }
                  </Typography>
                </Grid>

                <Grid
                  item
                  xs={6}
                >
                  <Typography variant="body2">
                    Date
                  </Typography>
                  <Typography fontWeight={600}>
                    {detailRecord.date
                      ? new Date(
                          detailRecord.date
                        ).toLocaleDateString(
                          'en-US'
                        )
                      : '—'}
                  </Typography>
                </Grid>

                <Grid
                  item
                  xs={6}
                >
                  <Typography variant="body2">
                    Status
                  </Typography>

                  <Chip
                    icon={
                      getStatusMeta(
                        detailRecord.status
                      ).icon
                    }
                    label={
                      getStatusMeta(
                        detailRecord.status
                      ).label
                    }
                    color={
                      getStatusMeta(
                        detailRecord.status
                      ).color
                    }
                    size="small"
                  />
                </Grid>

                {detailRecord.time_in && (
                  <Grid
                    item
                    xs={6}
                  >
                    <Typography variant="body2">
                      Time In
                    </Typography>
                    <Typography fontWeight={600}>
                      {
                        detailRecord.time_in
                      }
                    </Typography>
                  </Grid>
                )}

                {detailRecord.time_out && (
                  <Grid
                    item
                    xs={6}
                  >
                    <Typography variant="body2">
                      Time Out
                    </Typography>
                    <Typography fontWeight={600}>
                      {
                        detailRecord.time_out
                      }
                    </Typography>
                  </Grid>
                )}

                {detailRecord.branch !=
                  null && (
                  <Grid
                    item
                    xs={6}
                  >
                    <Typography variant="body2">
                      Branch
                    </Typography>
                    <Typography fontWeight={600}>
                      {detailRecord.branch
                        ?.name ||
                        detailRecord.branch}
                    </Typography>
                  </Grid>
                )}

                {detailRecord.organization !=
                  null && (
                  <Grid
                    item
                    xs={6}
                  >
                    <Typography variant="body2">
                      Organization
                    </Typography>
                    <Typography fontWeight={600}>
                      {detailRecord.organization
                        ?.name ||
                        detailRecord.organization}
                    </Typography>
                  </Grid>
                )}

                {detailRecord.remark && (
                  <Grid
                    item
                    xs={12}
                  >
                    <Typography variant="body2">
                      Remark
                    </Typography>
                    <Typography fontWeight={600}>
                      {
                        detailRecord.remark
                      }
                    </Typography>
                  </Grid>
                )}

                {(detailRecord.created_at ||
                  detailRecord.updated_at) && (
                  <Grid
                    item
                    xs={12}
                  >
                    <Divider />
                    <Typography
                      variant="caption"
                      color="text.disabled"
                    >
                      {detailRecord.created_at &&
                        `Created: ${new Date(
                          detailRecord.created_at
                        ).toLocaleString()}`}
                      {detailRecord.created_at &&
                        detailRecord.updated_at &&
                        ' • '}
                      {detailRecord.updated_at &&
                        `Updated: ${new Date(
                          detailRecord.updated_at
                        ).toLocaleString()}`}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            ) : (
              <Typography>
                No record data available.
              </Typography>
            )}
          </CustomTableScrollbar>
        </Box>
      </Modal>

      {/* =========================================================
          SUMMARY MODAL
          ========================================================= */}

      <Modal
        open={
          summaryModalOpen
        }
        onClose={
          handleCloseSummaryModal
        }
      >
        <Box
          sx={{
            position:
              'absolute',
            top: '50%',
            left: '50%',
            transform:
              'translate(-50%, -50%)',
            width: {
              xs: '90%',
              sm: 480,
            },
            bgcolor:
              'background.paper',
            borderRadius:
              '16px',
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette
                  .primary.main,
              p: 2,
              display: 'flex',
              justifyContent:
                'space-between',
              alignItems:
                'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight: 700,
              }}
            >
              Attendance Summary
            </Typography>

            <IconButton
              onClick={
                handleCloseSummaryModal
              }
              sx={{
                color: '#fff',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3 }}>
            {summaryLoading ? (
              <Box
                sx={{
                  display:
                    'flex',
                  justifyContent:
                    'center',
                  py: 5,
                }}
              >
                <CircularProgress />
              </Box>
            ) : summaryStudent &&
              summaryData ? (
              <>
                <Typography
                  fontWeight={700}
                >
                  {
                    summaryStudent.name
                  }
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    mb: 2.5,
                  }}
                >
                  {
                    summaryStudent.admissionCode
                  }
                </Typography>

                <Grid
                  container
                  spacing={2}
                >
                  {[
                    [
                      'workingDays',
                      'Working Days',
                      'text.primary',
                    ],
                    [
                      'presentDays',
                      'Present',
                      'success.main',
                    ],
                    [
                      'absentDays',
                      'Absent',
                      'error.main',
                    ],
                    [
                      'halfDays',
                      'Half Day',
                      'warning.main',
                    ],
                    [
                      'leaveDays',
                      'On Leave',
                      'info.main',
                    ],
                    [
                      'attendancePercentage',
                      'Attendance Rate',
                      'primary.main',
                    ],
                  ].map(
                    ([
                      key,
                      label,
                      color,
                    ]) => {
                      if (
                        (key ===
                          'halfDays' ||
                          key ===
                            'leaveDays') &&
                        summaryData[
                          key
                        ] <= 0
                      ) {
                        return null;
                      }

                      return (
                        <Grid
                          item
                          xs={6}
                          sm={4}
                          key={key}
                        >
                          <Box
                            sx={{
                              textAlign:
                                'center',
                              p: 1.5,
                              borderRadius:
                                '10px',
                              backgroundColor:
                                theme
                                  .palette
                                  .mode ===
                                'dark'
                                  ? 'rgba(255,255,255,0.05)'
                                  : 'rgba(0,0,0,0.03)',
                            }}
                          >
                            <Typography
                              variant="h5"
                              fontWeight={
                                700
                              }
                              color={
                                color
                              }
                            >
                              {
                                summaryData[
                                  key
                                ]
                              }
                              {key ===
                                'attendancePercentage' &&
                                '%'}
                            </Typography>

                            <Typography variant="caption">
                              {label}
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    }
                  )}
                </Grid>
              </>
            ) : (
              <Typography>
                No summary data available.
              </Typography>
            )}
          </Box>
        </Box>
      </Modal>

      {/* =========================================================
          USER GUIDE
          ========================================================= */}

      <Modal
        open={
          showUserGuideModal
        }
        onClose={() =>
          setShowUserGuideModal(
            false
          )
        }
      >
        <Box
          sx={{
            position:
              'absolute',
            top: '50%',
            left: '50%',
            transform:
              'translate(-50%, -50%)',
            width: {
              xs: '90%',
              sm: 700,
            },
            bgcolor:
              'background.paper',
            borderRadius:
              '16px',
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette
                  .primary.main,
              p: 2,
              display: 'flex',
              justifyContent:
                'space-between',
              alignItems:
                'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight: 700,
              }}
            >
              Attendance — User Guide
            </Typography>

            <IconButton
              onClick={() =>
                setShowUserGuideModal(
                  false
                )
              }
              sx={{
                color: '#fff',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <CustomTableScrollbar
            sx={{
              maxHeight:
                '70vh',
              overflowY:
                'auto',
              p: 3,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color:
                  theme.palette
                    .primary
                    .main,
                mb: 1.5,
              }}
            >
              ➤ Filtering Students
            </Typography>

            <ul
              style={{
                marginLeft:
                  '20px',
                lineHeight:
                  '2',
              }}
            >
              <li>
                Use the{' '}
                <strong>
                  Branch
                </strong>{' '}
                dropdown to filter
                students by branch.
              </li>
              <li>
                After selecting a
                branch, the{' '}
                <strong>
                  Course
                </strong>{' '}
                dropdown shows only
                courses belonging to
                that branch.
              </li>
              <li>
                After selecting a
                course, you can filter
                by{' '}
                <strong>
                  Batch
                </strong>
                .
              </li>
              <li>
                Use the{' '}
                <strong>
                  Search
                </strong>{' '}
                bar to find students.
              </li>
            </ul>

            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color:
                  theme.palette
                    .primary
                    .main,
                mb: 1.5,
                mt: 2,
              }}
            >
              ➤ Adding / Marking Attendance
            </Typography>

            <ul
              style={{
                marginLeft:
                  '20px',
                lineHeight:
                  '2',
              }}
            >
              <li>
                This page shows a
                read-only attendance
                view.
              </li>
              <li>
                Click{' '}
                <strong>
                  Add Attendance
                </strong>{' '}
                to mark attendance.
              </li>
            </ul>

            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color:
                  theme.palette
                    .primary
                    .main,
                mb: 1.5,
                mt: 2,
              }}
            >
              ➤ Attendance History
            </Typography>

            <ul
              style={{
                marginLeft:
                  '20px',
                lineHeight:
                  '2',
              }}
            >
              <li>
                Click the eye icon to
                view attendance
                history.
              </li>
              <li>
                Use From / To dates to
                filter history.
              </li>
              <li>
                Click the bar-chart
                icon for attendance
                summary.
              </li>
              <li>
                Click the pencil icon
                to edit an existing
                attendance record.
              </li>
            </ul>

            <Typography
              variant="body2"
              sx={{
                mt: 2,
                p: 1.5,
                borderLeft:
                  `3px solid ${theme.palette.warning.main}`,
                bgcolor:
                  theme.palette
                    .mode ===
                  'dark'
                    ? 'rgba(255,152,0,0.08)'
                    : 'rgba(255,152,0,0.06)',
              }}
            >
              ⚠️ Changing the date,
              branch, course or batch
              reloads the attendance
              data.
            </Typography>
          </CustomTableScrollbar>
        </Box>
      </Modal>
    </Container>
  );
};

export default Attendance;